import axios, { AxiosError } from 'axios';
import { getToken, clearAuth } from './auth';
import { toast } from './toast';

// Vite picks this up from .env at build time. Fall back to the backend's
// default dev port (3002 — set in main.ts) so a fresh clone works
// without an .env file.
const BASE_URL =
  (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:3002';

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
});

// Stamp every request with the JWT if we have one. Public endpoints
// don't care that the header is present.
api.interceptors.request.use((config) => {
  try {
    const token = getToken();
    if (token) {
      config.headers = config.headers ?? {};
      (config.headers as any).Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    // Silently fail on token retrieval — the endpoint will reject if needed.
    console.error('Failed to get token for request:', e);
  }
  return config;
});

// Login is allowed to return 401 on bad credentials — the form handles
// its own message and we don't want to auto-logout on that.
const FORM_OWNED_401_PATTERNS = ['/login'];

// Background polling endpoints. A 401 here should reject the promise
// so callers can swallow it; it should NOT wipe the session, because
// (a) a flaky poll shouldn't sign the user out of a working session,
// and (b) the next user-initiated call will trigger auto-logout
// anyway if the token is genuinely invalid.
const SILENT_401_PATTERNS = [
  '/me/notifications/unread-count',
  '/me/notifications',
];

api.interceptors.response.use(
  (res) => res,
  (err: AxiosError) => {
    try {
      if (err.response?.status === 401) {
        const url = (err.config?.url ?? '').toString();
        const isFormOwned = FORM_OWNED_401_PATTERNS.some((p) => url.includes(p));
        const isSilent = SILENT_401_PATTERNS.some((p) => url.includes(p));
        if (!isFormOwned && !isSilent) {
          // Surface the backend's auth message before redirecting so the
          // user (and devs) actually see *why* they got bounced — was
          // it a revoked session, an expired token, a missing jti, etc.
          // Without this toast the page navigates away so fast that the
          // body of the 401 is invisible.
          const body: any = err.response?.data;
          const reason =
            (body && typeof body === 'object' && body.message) ||
            'Your session is no longer valid. Please sign in again.';
          try {
            toast.error(`Signed out: ${reason}`);
          } catch {
            // toast not available — fall through to the redirect anyway.
          }
          try {
            clearAuth();
          } catch (e) {
            console.error('Failed to clear auth:', e);
          }
          if (
            typeof window !== 'undefined' &&
            !window.location.pathname.includes('/signin')
          ) {
            // Small delay so the toast has a chance to render before we
            // navigate away. Keeps the diagnostic visible.
            setTimeout(() => {
              try {
                window.location.href = '/professional/signin';
              } catch (e) {
                console.error('Failed to redirect to signin:', e);
              }
            }, 800);
          }
        }
      }
    } catch (e) {
      console.error('Error in response interceptor:', e);
    }
    return Promise.reject(err);
  },
);

// A raw, un-translated i18n key (e.g. "messages.booking.voucherNotYours")
// leaks through when the backend's message catalogue is missing that key
// (typically a deploy lag). Never show that to a user — fall back to a
// friendly message instead.
export function isRawI18nKey(msg: unknown): boolean {
  return typeof msg === 'string' && /^messages\.[\w.]+$/.test(msg);
}

export function apiErrorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  const e = err as AxiosError<any>;
  const msg = e?.response?.data?.message || (e as any)?.message;
  if (!msg || isRawI18nKey(msg)) return fallback;
  return msg;
}
